import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, Outlet } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SiteSettingsContext = React.createContext(null);

/** Public layout — no auth required. Shared nav + footer.
 *  Nav links are pulled from the CMS `cms_navigation` doc so the user can edit
 *  the menu without a redeploy.
 */
export default function PublicLayout() {
  const [settings, setSettings] = useState(null);
  const [nav, setNav] = useState([]);
  const [navOpen, setNavOpen] = useState(null); // dropdown id

  useEffect(() => {
    axios.get(`${API}/public/site-settings`)
      .then(res => setSettings(res.data))
      .catch(() => setSettings({
        company_name: 'Elite Home Rehab',
        primary_color: '#1a3a5c',
        accent_color: '#22c55e',
      }));
    axios.get(`${API}/public/cms/navigation`)
      .then(res => setNav(res.data?.items || []))
      .catch(() => setNav([]));
  }, []);

  // Apply favicon override when settings load
  useEffect(() => {
    if (!settings?.favicon_url) return;
    let link = document.querySelector("link[rel*='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = settings.favicon_url;
  }, [settings?.favicon_url]);

  if (!settings) {
    return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;
  }

  const primary = settings.primary_color || '#1a3a5c';

  const itemTarget = (it) => {
    if (it.external_url) return it.external_url;
    if (it.slug) return it.slug === 'home' ? '/' : `/${it.slug}`;
    return null;
  };

  return (
    <SiteSettingsContext.Provider value={settings}>
      <div className="min-h-screen flex flex-col bg-[#fafaf7]" style={{ '--site-primary': primary }}>
        <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b">
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2" data-testid="public-home-link">
              {settings.logo_url ? (
                <img
                  src={settings.logo_url}
                  alt={settings.company_name}
                  className="h-9 w-auto max-w-[220px] object-contain"
                />
              ) : (
                <>
                  <div
                    className="w-9 h-9 rounded-md grid place-items-center text-white font-bold"
                    style={{ backgroundColor: primary }}
                  >
                    {(settings.company_name || 'E')[0]}
                  </div>
                  <span className="font-semibold text-lg hidden sm:inline" style={{ color: primary }}>
                    {settings.company_name}
                  </span>
                </>
              )}
            </Link>
            <nav className="flex items-center gap-1 sm:gap-2">
              {nav.map((it) => {
                const target = itemTarget(it);
                const hasChildren = (it.children || []).length > 0;
                if (hasChildren) {
                  return (
                    <div
                      key={it.id}
                      className="relative"
                      onMouseEnter={() => setNavOpen(it.id)}
                      onMouseLeave={() => setNavOpen(null)}
                    >
                      <button
                        type="button"
                        className="px-3 py-1.5 rounded-md text-sm font-medium hover:bg-muted transition-colors"
                        onClick={() => setNavOpen((o) => (o === it.id ? null : it.id))}
                        data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {it.label} ▾
                      </button>
                      {navOpen === it.id && (
                        <div className="absolute right-0 top-full mt-1 min-w-[180px] bg-white border rounded-md shadow-lg py-1 z-50">
                          {(it.children || []).map((c) => {
                            const ct = itemTarget(c);
                            return (
                              <Link
                                key={c.id}
                                to={ct || '#'}
                                className="block px-3 py-2 text-sm hover:bg-muted"
                                onClick={() => setNavOpen(null)}
                              >
                                {c.label}
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }
                if (!target) return null;
                if (target.startsWith('http')) {
                  return (
                    <a
                      key={it.id}
                      href={target}
                      className="px-3 py-1.5 rounded-md text-sm font-medium hover:bg-muted transition-colors"
                      data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {it.label}
                    </a>
                  );
                }
                return (
                  <Link
                    key={it.id}
                    to={target}
                    className="px-3 py-1.5 rounded-md text-sm font-medium hover:bg-muted transition-colors"
                    data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {it.label}
                  </Link>
                );
              })}
              <Link
                to="/login"
                className="ml-1 px-3 py-1.5 rounded-md text-sm font-medium border hover:bg-muted transition-colors"
                data-testid="public-login-link"
              >
                Staff Login
              </Link>
              <Link
                to="/careers"
                className="px-4 py-1.5 rounded-md text-sm font-semibold text-white transition-opacity hover:opacity-90"
                style={{ backgroundColor: primary }}
                data-testid="public-apply-cta"
              >
                Apply Now
              </Link>
            </nav>
          </div>
        </header>

        <main className="flex-1">
          <Outlet />
        </main>

        <footer className="border-t bg-white/60 mt-16">
          <div className="max-w-6xl mx-auto px-4 py-6 flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground">
            <div>© {new Date().getFullYear()} {settings.company_name}. All rights reserved.</div>
            <div className="flex items-center gap-4">
              {settings.contact_email && <a href={`mailto:${settings.contact_email}`} className="hover:text-foreground">{settings.contact_email}</a>}
              {settings.contact_phone && <span>{settings.contact_phone}</span>}
              <Link to="/careers" className="hover:text-foreground">Careers</Link>
            </div>
          </div>
        </footer>
      </div>
    </SiteSettingsContext.Provider>
  );
}
